/* globals KATE_APP_NAME */
/* eslint no-param-reassign: "off" */
import { defineStore } from 'pinia';
import getEnvironmentConfig from '../get-environment-config';

const envConfig = getEnvironmentConfig();

function getDefaultState() {
  return {
    currentGroup: undefined,
    username: undefined,
    userId: undefined,
    userFullName: undefined,
    userPreferredName: undefined,
    userCoaches: [],
    userDataMentors: [],
    userLineManagers: [],
    userMentees: [],
    userPermissions: [],
    userEmail: undefined,
    userUln: undefined,
    appName: KATE_APP_NAME,
    appUrl: envConfig.appUrl,
    dashboardUrl: envConfig.dashboardUrl,
    avatarUrl: undefined,
    termsAccepted: false,
    notebookReady: false,
    inMaintenance: false,
    hideReadOnlyNotebookModal: false,
    inScreenshotMode: false,
    screenshotBlob: undefined,
    isSidebarOpen: true,
    sortModulesDescending: false,
    isGridLayout: false,
    environment: envConfig.environment,
    learnerSupportType: undefined,
    isChatButtonShowing: true,
  };
}

const useGlobalStore = defineStore('global', {
  state: () => getDefaultState(),

  // Identical to getters in vuex
  getters: {
    isApp(state) {
      return state.appName === 'app';
    },
    isDashboard(state) {
      return state.appName === 'dashboard';
    },
    isLoggedIn(state) {
      return Boolean(state.userId);
    },
    sidebarClass(state) {
      return `sidebar-page-content ${state.isSidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}`;
    },
    chatButtonIconClass(state) {
      return state.isChatButtonShowing ? 'fad fa-chevrons-down' : 'fad fa-chevrons-up';
    },
  },

  // Equivalent to mutations in vuex - but don't need state as an argument for all the action methods
  actions: {
    // TODO: This is not really logging in, this is loading user data into the store...
    logIn(payload) {
      this.username = payload.username;
      this.userId = payload.id;
      this.termsAccepted = Boolean(payload.terms_and_conditions_accepted_on);
      this.userFullName = payload.full_name;
      this.userPreferredName = payload.preferred_name;
      this.userEmail = payload.email;
      this.userUln = payload.uln;
      this.userCoaches = payload.coaches ?? [];
      this.userDataMentors = payload.data_mentors ?? [];
      this.userLineManagers = payload.line_managers ?? [];
      this.userMentees = payload.mentees;
      this.avatarUrl = payload.avatar_url;
      this.userPermissions = payload.user_permissions;
      this.learnerSupportType = payload.learner_support_type;
    },
    logOut() {
      // Don't have access to app level .$posthog instance here, so use the global one to reset on logout
      if (window.posthog?.reset) {
        // Clears the in-memory PostHog client state on that tab
        window.posthog.reset();
      }
      Object.assign(this, getDefaultState());
      localStorage.clear();
    },

    // All the actions below are basically redundant as we can mutate the store state directly
    setMaintenance() {
      this.inMaintenance = true;
    },
    unsetMaintenance() {
      this.inMaintenance = false;
    },
    acceptTerms() {
      this.termsAccepted = true;
    },
    updatePreferredName(payload) {
      this.userPreferredName = payload;
    },
    enterScreenshotMode() {
      this.inScreenshotMode = true;
    },
    exitScreenshotMode() {
      this.inScreenshotMode = false;
    },
    addScreenshotBlob(payload) {
      this.screenshotBlob = payload;
    },
    clearScreenshotBlob() {
      this.screenshotBlob = undefined;
    },
    notebookReadyToggle() {
      this.notebookReady = !this.notebookReady;
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    setSidebar(payload) {
      this.isSidebarOpen = payload;
    },
    toggleModulesSortingOrder() {
      this.sortModulesDescending = !this.sortModulesDescending;
    },
    setModulesSorting(payload) {
      this.sortModulesDescending = payload;
    },
    toggleModulesLayout() {
      this.isGridLayout = !this.isGridLayout;
    },
    setModulesLayout(payload) {
      this.isGridLayout = payload;
    },
    toggleChatButton() {
      this.isChatButtonShowing = !this.isChatButtonShowing;
    },
    setChatButton(payload) {
      this.isChatButtonShowing = payload;
    },
  },
});

export default useGlobalStore;
